import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import WrappedPaymentForm from './PaymentForm';
import '../styles.css';

function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialPlan = searchParams.get('plan') === 'single' ? 'single' : 'pack5';

  const [userId, setUserId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan); // 'single' ($39) or 'pack5' ($79)
  const BASE_API_URL = 'https://h1n5dryzeg.execute-api.us-east-1.amazonaws.com/prod';

  const expectedCredits = selectedPlan === 'single' ? 1 : 5;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        const email = attributes.email;
        setUserId(email);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Please log in to purchase.');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  const fetchCreditsWithRetry = async (maxRetries = 3, delayMs = 2000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`Fetching credits (attempt ${attempt}/${maxRetries})`);
      try {
        const response = await fetch(`${BASE_API_URL}/user-credits?userId=${userId}`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch credits: ${response.status} - ${errorText}`);
        }
        const data = await response.json();
        console.log(`Credits fetched: ${data.credits}`);
        if (data.credits >= expectedCredits) {
          return data.credits;
        }
        console.log(`Credits (${data.credits}) not updated yet, retrying...`);
      } catch (err) {
        console.error(`Error fetching credits (attempt ${attempt}):`, err);
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    console.warn('Max retries reached, credits may not have updated.');
    return 0;
  };

  const handlePaymentSuccess = async () => {
    console.log('Payment succeeded, fetching credits');
    try {
      const credits = await fetchCreditsWithRetry();
      navigate('/dashboard', {
        state: { paymentSuccess: true, credits },
      });
    } catch (err) {
      console.error('Critical error during credit fetch:', err);
      navigate('/dashboard', {
        state: { paymentSuccess: true, creditsFetchError: 'Credits may not have updated. Please refresh the dashboard.' },
      });
    }
  };

  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200/60 shadow-sm py-4 px-6 flex justify-between items-center">
        <a href="/">
          <img src="/Casalytix Logo.png" alt="Casalytix Logo" style={{ height: '40px' }} />
        </a>
        <a href="/dashboard" className="text-slate-600 hover:text-sky-500 font-semibold transition-colors no-underline">
          Back to Dashboard
        </a>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto flex flex-col gap-8 py-12 px-6">
        {/* Plan Selector Header */}
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-3">Choose Your Report Package</h1>
          <p className="text-slate-600 text-sm">
            All credits include full property comps, market analysis charts, and an editable Word document (.doc) Property Tax Appeal Report. Credits never expire.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-6 text-left">
            {/* Single Report Card Option */}
            <div
              onClick={() => setSelectedPlan('single')}
              className={`cursor-pointer rounded-2xl p-5 border-2 transition-all ${
                selectedPlan === 'single'
                  ? 'border-sky-500 bg-white shadow-md ring-2 ring-sky-500/20'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-slate-900 text-lg">Single Report</span>
                <span className="text-2xl font-black text-slate-900">$39</span>
              </div>
              <p className="text-xs text-slate-500 mb-0">1 Report Credit — Perfect for a single property appeal.</p>
            </div>

            {/* 5-Report Pack Card Option */}
            <div
              onClick={() => setSelectedPlan('pack5')}
              className={`cursor-pointer rounded-2xl p-5 border-2 relative transition-all ${
                selectedPlan === 'pack5'
                  ? 'border-sky-500 bg-slate-900 text-white shadow-lg ring-2 ring-sky-500/20'
                  : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300'
              }`}
            >
              <span className="absolute -top-3 right-4 bg-sky-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                Best Value (Save 60%)
              </span>
              <div className="flex justify-between items-center mb-2">
                <span className={`font-bold text-lg ${selectedPlan === 'pack5' ? 'text-white' : 'text-slate-900'}`}>5-Report Pack</span>
                <span className={`text-2xl font-black ${selectedPlan === 'pack5' ? 'text-white' : 'text-slate-900'}`}>$79</span>
              </div>
              <p className={`text-xs mb-0 ${selectedPlan === 'pack5' ? 'text-slate-300' : 'text-slate-500'}`}>
                5 Report Credits ($15.80/report) — Unused credits never expire.
              </p>
            </div>
          </div>
        </div>

        {/* Main Checkout Section */}
        <div className="flex flex-col md:flex-row gap-8 items-stretch mt-4">
          {/* Left Column: Plan Summary */}
          <div className="flex-1 bg-slate-900 rounded-3xl p-8 text-white flex flex-col justify-between shadow-xl">
            <div>
              <div className="mb-6">
                <span className="bg-sky-500/20 text-sky-400 text-xs font-black px-3 py-1.5 rounded-full uppercase tracking-wider">
                  Selected Package
                </span>
              </div>
              <h2 className="text-3xl font-extrabold mb-2 text-white">
                {selectedPlan === 'single' ? 'Single Property Report' : '5-Report Pack'}
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                {selectedPlan === 'single'
                  ? 'Includes 1 Report Credit to unlock full comps and generate an editable Word document (.doc) property tax appeal report.'
                  : 'Includes 5 Report Credits to unlock property comps and generate editable Word document (.doc) tax appeal reports anytime.'}
              </p>
              <ul className="space-y-4 mb-6 list-none p-0">
                <li className="flex items-center gap-3 text-slate-200">
                  <svg className="w-5 h-5 text-sky-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  <span>{selectedPlan === 'single' ? '1 Report Credit (Never Expires)' : '5 Report Credits (Never Expire)'}</span>
                </li>
                <li className="flex items-center gap-3 text-slate-200">
                  <svg className="w-5 h-5 text-sky-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  <span>Detailed Comparable Sales (Up to 50 Comps)</span>
                </li>
                <li className="flex items-center gap-3 text-slate-200">
                  <svg className="w-5 h-5 text-sky-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  <span>Editable Word Doc Appeal Report (.doc)</span>
                </li>
                <li className="flex items-center gap-3 text-slate-200">
                  <svg className="w-5 h-5 text-sky-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  <span>Market Trend & Volume Charts</span>
                </li>
              </ul>
            </div>
            <div className="border-t border-slate-800 pt-6 mt-6">
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-slate-400 font-medium">Total Due Now</span>
                <span className="text-3xl font-black text-white">{selectedPlan === 'single' ? '$39.00' : '$79.00'}</span>
              </div>
              <p className="text-slate-500 text-xs italic m-0">One-time payment. No subscription or recurring fees.</p>
            </div>
          </div>

          {/* Right Column: Checkout Form */}
          <div className="flex-1 bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-6">Payment Details</h3>
              {userId && (
                <WrappedPaymentForm
                  userId={userId}
                  onSuccess={handlePaymentSuccess}
                  setError={setError}
                  selectedPlan={selectedPlan}
                />
              )}
            </div>
            {error && (
              <div className="mt-6 p-4 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-sm font-medium">
                {error}
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="w-full bg-slate-100 py-8 border-t border-slate-200 mt-auto">
        <div className="max-w-6xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Casalytix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Payment;